import {Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {JsBridgeService} from './js-bridge.service';
import {LocalStorageService, StorageProperties} from './local-storage.service';
import {PasskeyWebAuthnService} from '../core/api/generated';
import {LoginResponse} from '../core/api/generated';
import {PasskeyRegistrationOptionsResponse} from '../core/api/generated';
import {PasskeyStatusResponse} from '../core/api/generated';

export interface PasskeyHint {
  /** Handle opaco dell'utente (user_id Base64URL emesso da /register/options). */
  userHandle: string;
  displayName: string;
}

/**
 * Esito della verifica dell'hint locale contro il backend.
 *
 * I quattro casi sono **distinti nel tipo** e non deducibili da un booleano, perché due di essi
 * richiedono azioni opposte sullo stesso dato: `invalid` cancella l'hint, `unreachable` lo
 * preserva. Fonderli in un unico `catch` è il difetto che questo tipo esiste per impedire — un
 * errore di rete transitorio cancellava l'hint in modo definitivo, costringendo l'utente a
 * ri-registrare la passkey dal Profilo.
 */
export type HintCheckOutcome =
  /** Il backend conferma: la passkey è utilizzabile. */
  | {kind: 'valid'; hint: PasskeyHint}
  /** Il backend ha risposto che la passkey non è più valida: l'hint è già stato cancellato. */
  | {kind: 'invalid'}
  /**
   * Non è stato possibile chiedere (rete assente, backend giù): l'hint è stato PRESERVATO ed è
   * riportato qui, così il chiamante può offrire un nuovo tentativo senza rileggere lo storage.
   */
  | {kind: 'unreachable'; hint: PasskeyHint}
  /** Nessun hint salvato, o hint malformato/legacy: niente da preservare né da cancellare. */
  | {kind: 'none'};


function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

@Injectable({providedIn: 'root'})
export class PasskeyService {

  private cachedRegistrationOptions: PasskeyRegistrationOptionsResponse | null = null;
  private cachedAvailable: boolean | null = null;

  constructor(
    private jsBridgeService: JsBridgeService,
    private localStorageService: LocalStorageService,
    private passkeyApi: PasskeyWebAuthnService,
  ) {}

  async isAvailable(): Promise<boolean> {
    if (this.cachedAvailable !== null) {
      return this.cachedAvailable;
    }

    const result = await this.detectWebAuthnSupport();
    this.cachedAvailable = result;
    return result;
  }

  private async detectWebAuthnSupport(): Promise<boolean> {
    // In contesto nativo (app iOS/Android con bridge JS) NON si usa WebAuthn nella WebView
    // (inaffidabile): le passkey passano dall'API nativa di credenziali via bridge, e la
    // disponibilità la decide il guscio. Il ponte esiste su ENTRAMBE le piattaforme (Android:
    // Credential Manager; iOS: ASAuthorization + Associated Domains); nel web reale si usano i
    // livelli di detection qui sotto.
    if (this.jsBridgeService.isNativeContext()) {
      return await this.jsBridgeService.isPasskeySupported();
    }

    // Check base: API WebAuthn esiste?
    if (!navigator.credentials?.create || !(window as any).PublicKeyCredential) {
      return false;
    }

    const PKC = (window as any).PublicKeyCredential;

    // Livello 1: getClientCapabilities (Chrome 133+, Safari 17.4+, Firefox 135+)
    try {
      if (typeof PKC.getClientCapabilities === 'function') {
        const caps = await PKC.getClientCapabilities();
        if (caps.passkeyPlatformAuthenticator === true
          || caps.userVerifyingPlatformAuthenticator === true) {
          return true;
        }
        return false;
      }
    } catch (e) {
      console.warn('[Passkey] getClientCapabilities() failed, falling back', e);
    }

    // Livello 2: isUVPAA (legacy, ampio supporto)
    try {
      if (typeof PKC.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
        return await PKC.isUserVerifyingPlatformAuthenticatorAvailable();
      }
    } catch (e) {
      console.warn('[Passkey] isUVPAA() failed, falling back', e);
    }

    // Livello 3: l'API esiste, proviamo
    return true;
  }

  async prefetchRegistrationOptions(): Promise<void> {
    try {
      this.cachedRegistrationOptions = await firstValueFrom(
        this.passkeyApi.getPasskeyRegisterOptions(),
      );
    } catch (e) {
      console.warn('[Passkey] prefetch registration options failed', e);
    }
  }

  /**
   * Registra una passkey e restituisce l'handle opaco dell'utente (`user_id`),
   * da salvare come hint per i successivi login/check (il BE non usa più l'email).
   */
  async register(userName?: string, refreshToken?: string | null, deviceLabel?: string): Promise<string> {
    let optionsRes: PasskeyRegistrationOptionsResponse;
    if (this.cachedRegistrationOptions) {
      optionsRes = this.cachedRegistrationOptions;
      this.cachedRegistrationOptions = null;
    } else {
      console.warn('[Passkey] options not pre-fetched, falling back to sync fetch');
      optionsRes = await firstValueFrom(
        this.passkeyApi.getPasskeyRegisterOptions(),
      );
    }

    // `user.name`/`displayName` sono campi obbligatori WebAuthn: se mancano,
    // navigator.credentials.create() rifiuta con un TypeError criptico.
    // Lo spec dichiara `user_name` obbligatorio, ma il BE può ometterlo:
    // usiamo il displayName passato dal chiamante come fallback (è solo
    // un'etichetta leggibile nel gestore passkey).
    const resolvedUserName: string | undefined = optionsRes.user_name || userName;
    if (!resolvedUserName) {
      console.error('[Passkey] registration options prive di user_name e nessun fallback disponibile', optionsRes);
      throw new Error('Passkey registration options missing user name');
    }

    // Contesto nativo: il Credential Manager sostituisce navigator.credentials.create.
    if (this.jsBridgeService.isNativeContext()) {
      const {attestation_object, client_data_json} =
        await this.registerNative(optionsRes, resolvedUserName);
      await firstValueFrom(
        this.passkeyApi.verifyPasskeyRegister({
          passkeyRegistrationVerifyForm: {
            attestation_object,
            client_data_json,
            display_name: deviceLabel || 'Il mio dispositivo',
            refresh_token: refreshToken || '',
          },
        }),
      );
      return optionsRes.user_id;
    }

    const createOptions: CredentialCreationOptions = {
      publicKey: {
        challenge: base64urlToBuffer(optionsRes.challenge),
        rp: {id: optionsRes.rp_id, name: optionsRes.rp_name},
        user: {
          id: base64urlToBuffer(optionsRes.user_id),
          name: resolvedUserName,
          displayName: resolvedUserName,
        },
        pubKeyCredParams: [
          {alg: -7, type: 'public-key'},   // ES256
          {alg: -257, type: 'public-key'},  // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'preferred',
          residentKey: 'preferred',
        },
      },
    };

    let credential: PublicKeyCredential;
    try {
      credential = await navigator.credentials.create(createOptions) as PublicKeyCredential;
    } catch (e: any) {
      console.error(`[Passkey] credentials.create() failed: ${e?.name} — ${e?.message}`);
      throw e;
    }
    if (!credential) {
      throw new Error('Registration cancelled');
    }

    const response = credential.response as AuthenticatorAttestationResponse;

    await firstValueFrom(
      this.passkeyApi.verifyPasskeyRegister({
        passkeyRegistrationVerifyForm: {
          attestation_object: bufferToBase64url(response.attestationObject),
          client_data_json: bufferToBase64url(response.clientDataJSON),
          display_name: deviceLabel || 'Il mio dispositivo',
          refresh_token: refreshToken || '',
        },
      }),
    );

    // L'handle opaco da usare per login/check successivi (il BE non usa più l'email).
    return optionsRes.user_id;
  }

  async login(userHandle: string): Promise<{access_token: string; refresh_token: string; expires_in: number; token_type: string}> {
    const optionsRes = await firstValueFrom(
      this.passkeyApi.getPasskeyLoginOptions({passkeyLoginOptionsForm: {user_handle: userHandle}}),
    );
    const sessionId = optionsRes.session_id;

    // Contesto nativo: il Credential Manager sostituisce navigator.credentials.get.
    if (this.jsBridgeService.isNativeContext()) {
      const assertion = await this.authenticateNative(optionsRes);
      const loginResNative: LoginResponse = await firstValueFrom(
        this.passkeyApi.verifyPasskeyLogin({
          passkeyLoginVerifyForm: {
            session_id: sessionId,
            credential_id: assertion.credential_id,
            authenticator_data: assertion.authenticator_data,
            client_data_json: assertion.client_data_json,
            signature: assertion.signature,
          },
        }),
      );
      if (!loginResNative.access_token) {
        throw new Error('Login failed: missing access_token');
      }
      return {
        access_token: loginResNative.access_token,
        refresh_token: loginResNative.refresh_token || '',
        expires_in: loginResNative.expires_in || 0,
        token_type: loginResNative.token_type || 'Bearer',
      };
    }

    const getOptions: CredentialRequestOptions = {
      publicKey: {
        challenge: base64urlToBuffer(optionsRes.challenge),
        rpId: optionsRes.rp_id,
        userVerification: 'preferred',
        allowCredentials: (optionsRes.allow_credentials || []).map(
          (id: string) => ({
            type: 'public-key' as PublicKeyCredentialType,
            id: base64urlToBuffer(id),
          }),
        ),
      },
    };

    let credential: PublicKeyCredential;
    try {
      credential = await navigator.credentials.get(getOptions) as PublicKeyCredential;
    } catch (e: any) {
      console.error(`[Passkey] credentials.get() failed: ${e?.name} — ${e?.message}`);
      throw e;
    }
    if (!credential) {
      throw new Error('Authentication cancelled');
    }

    const assertionResponse = credential.response as AuthenticatorAssertionResponse;

    const loginRes: LoginResponse = await firstValueFrom(
      this.passkeyApi.verifyPasskeyLogin({
        passkeyLoginVerifyForm: {
          session_id: sessionId,
          credential_id: bufferToBase64url(credential.rawId),
          authenticator_data: bufferToBase64url(assertionResponse.authenticatorData),
          client_data_json: bufferToBase64url(assertionResponse.clientDataJSON),
          signature: bufferToBase64url(assertionResponse.signature),
        },
      }),
    );

    if (!loginRes.access_token) {
      throw new Error('Login failed: missing access_token');
    }

    return {
      access_token: loginRes.access_token,
      refresh_token: loginRes.refresh_token || '',
      expires_in: loginRes.expires_in || 0,
      token_type: loginRes.token_type || 'Bearer',
    };
  }

  /**
   * Registrazione via Credential Manager nativo.
   *
   * Costruisce il `PublicKeyCredentialCreationOptions` in formato JSON WebAuthn (challenge e
   * `user.id` restano stringhe base64url, così come le emette il BE — il Credential Manager li
   * decodifica internamente), lo passa al bridge e ne estrae `attestationObject`/`clientDataJSON`
   * dal `registrationResponseJson` (già base64url, pronti per il verify).
   */
  private async registerNative(
    optionsRes: PasskeyRegistrationOptionsResponse,
    resolvedUserName: string,
  ): Promise<{attestation_object: string; client_data_json: string}> {
    const requestJson = JSON.stringify({
      challenge: optionsRes.challenge,
      rp: {id: optionsRes.rp_id, name: optionsRes.rp_name},
      user: {
        id: optionsRes.user_id,
        name: resolvedUserName,
        displayName: resolvedUserName,
      },
      pubKeyCredParams: [
        {type: 'public-key', alg: -7},    // ES256
        {type: 'public-key', alg: -257},  // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'preferred',
        residentKey: 'preferred',
      },
      timeout: 60000,
      attestation: 'none',
    });

    const responseJson = await this.jsBridgeService.startPasskeyRegistration(requestJson);
    const parsed = JSON.parse(responseJson);
    const response = parsed?.response;
    if (!response?.attestationObject || !response?.clientDataJSON) {
      throw new Error('Passkey registration response malformed');
    }
    return {
      attestation_object: response.attestationObject,
      client_data_json: response.clientDataJSON,
    };
  }

  /**
   * Autenticazione via Credential Manager nativo.
   *
   * Costruisce il `PublicKeyCredentialRequestOptions` in formato JSON WebAuthn e ne estrae i campi
   * dell'assertion dal `authenticationResponseJson` (base64url, pronti per il verify). `credential_id`
   * usa `parsed.rawId` (equivalente base64url di `credential.rawId` del path browser).
   */
  private async authenticateNative(
    optionsRes: {challenge: string; rp_id: string; allow_credentials?: string[]},
  ): Promise<{credential_id: string; authenticator_data: string; client_data_json: string; signature: string}> {
    const requestJson = JSON.stringify({
      challenge: optionsRes.challenge,
      rpId: optionsRes.rp_id,
      userVerification: 'preferred',
      timeout: 60000,
      allowCredentials: (optionsRes.allow_credentials || []).map((id: string) => ({
        type: 'public-key',
        id,
      })),
    });

    const responseJson = await this.jsBridgeService.startPasskeyAuthentication(requestJson);
    const parsed = JSON.parse(responseJson);
    const response = parsed?.response;
    const credentialId = parsed?.rawId || parsed?.id;
    if (!credentialId || !response?.authenticatorData || !response?.clientDataJSON || !response?.signature) {
      throw new Error('Passkey authentication response malformed');
    }
    return {
      credential_id: credentialId,
      authenticator_data: response.authenticatorData,
      client_data_json: response.clientDataJSON,
      signature: response.signature,
    };
  }

  async getStatus(): Promise<PasskeyStatusResponse> {
    return await firstValueFrom(this.passkeyApi.getPasskeyStatus());
  }

  async checkAvailability(userHandle: string): Promise<boolean> {
    const res = await firstValueFrom(
      this.passkeyApi.checkPasskeyDisponibile({userHandle}),
    );
    return res.available;
  }

  /**
   * Verifica l'hint locale col backend, distinguendo i casi che il chiamante deve trattare in
   * modo **opposto** (vedi {@link HintCheckOutcome}).
   *
   * <p>È l'unico punto di questo percorso che cancella l'hint, e lo fa **solo** quando il backend
   * ha risposto esplicitamente che non è più valido. Qualunque errore che non sia quella risposta
   * — rete assente (`status === 0`, stessa convenzione usata dai servizi delle feature cittadino),
   * timeout, 5xx — lascia l'hint al suo posto: non si distrugge una credenziale valida perché non
   * si è riusciti a chiedere.
   */
  async checkHint(): Promise<HintCheckOutcome> {
    const hint = await this.getHint();
    if (!hint) {
      return {kind: 'none'};
    }
    try {
      const available = await this.checkAvailability(hint.userHandle);
      if (!available) {
        await this.clearHint();
        return {kind: 'invalid'};
      }
      return {kind: 'valid', hint};
    } catch (err) {
      // Nessuna risposta utile dal backend: l'hint resta. Un 5xx non è una revoca, è un guasto.
      console.warn('[Passkey] checkHint: verifica non conclusa, hint preservato', err);
      return {kind: 'unreachable', hint};
    }
  }

  async revokeAll(): Promise<void> {
    await firstValueFrom(this.passkeyApi.deleteAllPasskey());
  }

  async deleteCredential(credentialId: string): Promise<void> {
    await firstValueFrom(this.passkeyApi.deletePasskey({credentialId}));
  }

  async saveHint(userHandle: string, displayName: string): Promise<void> {
    localStorage.setItem(StorageProperties.PASSKEY_HINT, JSON.stringify({userHandle, displayName}));
  }

  async getHint(): Promise<PasskeyHint | null> {
    const value = localStorage.getItem(StorageProperties.PASSKEY_HINT);
    if (!value) {
      return null;
    }
    try {
      const parsed = JSON.parse(value);
      // Hint legacy (pre-migrazione user_handle): salvava { email }. Senza handle
      // non è più utilizzabile col nuovo BE → lo scartiamo, fallback a SPID finché
      // la prossima registrazione non riemette l'handle.
      if (!parsed?.userHandle) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  async clearHint(): Promise<void> {
    localStorage.removeItem(StorageProperties.PASSKEY_HINT);
  }

  // --- Rifiuto della proposta automatica (per-utente, su questo dispositivo) ---

  /**
   * Registra il rifiuto della proposta di registrazione passkey per un utente.
   *
   * <p>Il rifiuto è **permanente ma locale a questo dispositivo**: vive in `localStorage`, quindi
   * chi salta sul telefono continua a vedersi proporre la registrazione su tablet o desktop. È la
   * semantica corretta per una credenziale per-dispositivo — il "no" risponde alla domanda «vuoi
   * l'accesso rapido *qui*?», non «vuoi le passkey in generale». Sullo stesso dispositivo non si
   * insiste più: riproporlo a ogni accesso darebbe l'incentivo sbagliato a registrare solo per far
   * sparire il popup. La strada resta comunque aperta dal Profilo, dove la registrazione manuale è
   * sempre disponibile.
   *
   * <p>La chiave contiene una **lista di id utente** e non un flag globale: su un dispositivo
   * condiviso (caso concreto in famiglia) il rifiuto di un genitore non deve sopprimere la
   * proposta per l'altro.
   */
  async marcaPromptRifiutato(userId: string): Promise<void> {
    if (!userId) {
      return;
    }
    const rifiutati = this.leggiPromptRifiutati();
    if (!rifiutati.includes(userId)) {
      rifiutati.push(userId);
      localStorage.setItem(StorageProperties.PASSKEY_PROMPT_RIFIUTATO, JSON.stringify(rifiutati));
    }
  }

  /** @returns true se questo utente ha già rifiutato la proposta su questo dispositivo. */
  async isPromptRifiutato(userId: string): Promise<boolean> {
    return !!userId && this.leggiPromptRifiutati().includes(userId);
  }

  /** Storage corrotto o di formato legacy → lista vuota: si riproporrà la registrazione. */
  private leggiPromptRifiutati(): string[] {
    const raw = localStorage.getItem(StorageProperties.PASSKEY_PROMPT_RIFIUTATO);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
    } catch {
      return [];
    }
  }
}
